'use client'

import { chainValidations, makeFormStateWithModel } from 'formtastisch'
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'
import { useBoolean } from 'usehooks-ts'

import { IconTouch } from '@redteclab/icons'

import {
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Link,
  LoadingButton,
} from 'base-ui'

import { validateEmail, validateRequired } from '../../../base-form-handling'
import {
  FEATURE_TOGGLE_ONE_TIME_PASSWORD,
  useFeatureToggleContext,
} from '../../../feature-toggles'
import { FriendlyCaptchaChallenge } from '../../../friendly-captcha'
import { useGlobalConfigContext } from '../../../global-config'
import { TENANT } from '../../../tenant'
import { urlResolverGetForgotPassword } from '../../../url-handling'
import { useUserApiLoginMutation } from '../../api/swr/useUserApiLoginMutation'
import {
  LOGIN_OTP_STATE,
  LoginRegistrationUserLoginStateContext,
} from '../../context/LoginRegistrationUserLoginStateContext'

import { LoginRegistrationUserLoginGeneralError } from './LoginRegistrationUserLoginGeneralError'
import { LoginRegistrationUserLoginShowPassword } from './LoginRegistrationUserLoginShowPassword'

const makeLoginRegistrationUserLoginFormState = (
  intl: IntlShape,
  username: string,
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- the rule was disabled before a comment became required
) => {
  return makeFormStateWithModel({
    initialData: {
      fcSolution: '',
      password: '',
      username,
    },
    validations: {
      password: (value) => {
        return validateRequired(
          value,
          intl.formatMessage({ id: 'form.error.input.missing' }),
        )
      },
      username: (value) => {
        return chainValidations(
          () =>
            validateRequired(
              value,
              intl.formatMessage({ id: 'form.error.input.missing' }),
            ),

          () =>
            validateEmail(
              value,
              intl.formatMessage({ id: 'error.email.invalid' }),
            ),
        )
      },
    },
  })
}

type LoginFormState = ReturnType<typeof makeLoginRegistrationUserLoginFormState>

const handleSubmit = async (
  loginSwr: ReturnType<typeof useUserApiLoginMutation>,
  formState: LoginFormState,
): Promise<void> => {
  if (loginSwr.isMutating) {
    return
  }

  formState.validate()
  if (!formState.isValid()) {
    return
  }

  const { model } = formState

  try {
    await loginSwr.trigger({
      fcSolution: model.fcSolution,
      password: model.password,
      username: model.username,
    })
  } catch {
    // void - error is handled in component (mutation error)
  }
}

interface LoginRegistrationUserLoginFormProps {
  /**
   * this will be run after user successfully logs in.
   * because some other side effects run after a user logs in (e.g. refetch minicart),
   * promise of those will be passed as argument, in case if you need to await for that.
   * otherwise do not care and handle without awaiting.
   */
  afterLoggedIn?: (
    afterLoggedInSideEffectsWithPreferredLanguage: Promise<string | undefined>,
  ) => void
}

export const LoginRegistrationUserLoginForm: FC<
  PropsWithChildren<LoginRegistrationUserLoginFormProps>
  // eslint-disable-next-line max-lines-per-function -- the rule was disabled before a comment became required
> = ({ afterLoggedIn, children }) => {
  const intl = useIntl()
  const globalConfig = useGlobalConfigContext()
  const { tenant } = globalConfig
  const featureToggles = useFeatureToggleContext()

  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)

  const { setLoginOtpState, setUsername, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )

  const isOneTimePasswordEnabled = Boolean(
    featureToggles[FEATURE_TOGGLE_ONE_TIME_PASSWORD],
  )
  const { toggle: toggleShowPassword, value: showPassword } = useBoolean(false)
  const formState = useMemo(
    () => makeLoginRegistrationUserLoginFormState(intl, username),
    [intl, username],
  ).use()
  const { model } = formState
  const loginSwr = useUserApiLoginMutation({
    onError: () => {
      formState.setValue('', model, 'fcSolution')
      model.password = ''
      formState.update()
    },
    onSuccess: afterLoggedIn,
  })
  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      formState.setValue(fcSolution, model, 'fcSolution')
      setIsFriendlyCaptchaDone(true)
    },
    [formState, model],
  )

  return (
    <>
      <h2 className="mb-6 text-l font-medium">
        {[TENANT.BE, TENANT.CH].includes(tenant) ? (
          <FormattedMessage id="login.loginBox.headlineRebrand" />
        ) : (
          <FormattedMessage id="login.loginBox.headLine" />
        )}
      </h2>
      <form
        data-qa-id="loginModal"
        onSubmit={(event): void => {
          event.preventDefault()
          setUsername(model.username)
          void handleSubmit(loginSwr, formState)
        }}
      >
        {children}
        {[TENANT.BE, TENANT.CH, TENANT.FR].includes(tenant) && (
          <p className="mb-5">
            {intl.formatMessage({ id: 'login.rebrandingInfo' })}
          </p>
        )}
        {loginSwr.error ? (
          <LoginRegistrationUserLoginGeneralError
            className="mb-3.5"
            error={loginSwr.error}
          />
        ) : null}
        <FormControl
          className="mb-3.5"
          error={!model.validator.isPropertyValid('username')}
          {...formState.makeInputProps('username', { validateOnBlur: true })}
          value={model.username}
        >
          <FormLabel htmlFor="login-modal-email">
            {intl.formatMessage({ id: 'general.label.email' })}
          </FormLabel>
          <Input
            id="login-modal-email"
            slotProps={{
              input: { 'data-qa-id': 'login-form-username' },
            }}
          />
          {model.validator.getFirstErrorFor('username') && (
            <FormHelperText data-qa-id="loginForm-eMail-error-msg">
              {model.validator.getFirstErrorFor('username')}
            </FormHelperText>
          )}
        </FormControl>
        <FormControl
          className="mb-3.5"
          error={!model.validator.isPropertyValid('password')}
          onChange={(event): void => {
            formState.setValue(event.target.value, model, 'password')
          }}
          value={model.password}
        >
          <FormLabel htmlFor="login-modal-password">
            {intl.formatMessage({ id: 'general.label.password' })}
          </FormLabel>
          <Input
            endAdornment={
              <LoginRegistrationUserLoginShowPassword
                showPassword={showPassword}
                toggleShowPassword={toggleShowPassword}
              />
            }
            id="login-modal-password"
            slotProps={{
              input: { 'data-qa-id': 'login-form-password' },
            }}
            type={showPassword ? 'text' : 'password'}
          />
          {model.validator.getFirstErrorFor('password') && (
            <FormHelperText data-qa-id="loginForm-password-error-msg">
              {model.validator.getFirstErrorFor('password')}
            </FormHelperText>
          )}
        </FormControl>
        {isOneTimePasswordEnabled ? (
          <div className="mt-4">
            <Link
              className="flex cursor-pointer flex-row items-center font-medium no-underline"
              color="inherit"
              data-qa-id="login-form-otp-request"
              onClick={(event): void => {
                event.preventDefault()
                setUsername(model.username)
                setLoginOtpState(LOGIN_OTP_STATE.OTP_REQUEST)
              }}
            >
              <IconTouch className="mx-2 size-8 fill-dark-brand" />
              <div className="text-m font-normal text-dark-primary-max hover:text-dark-hover-brand">
                <FormattedMessage id="login.otp.requestLoginCode" />
              </div>
            </Link>
          </div>
        ) : null}
        <FriendlyCaptchaChallenge
          className="mb-2"
          doneCallback={captchaDoneCallback}
          startMode="auto"
          whenThisPropChangesResetCaptcha={loginSwr.error}
        />
        <LoadingButton
          className="w-full"
          data-qa-id="login-form-loginForm-submit-button"
          disabled={!isFriendlyCaptchaDone || loginSwr.isMutating}
          loading={loginSwr.isMutating}
          loadingPosition="end"
          type="submit"
        >
          <FormattedMessage id="general.label.subscribe" />
        </LoadingButton>
        <div className="mt-5 text-center">
          <Link
            className="font-medium"
            data-qa-id="login-form-forgot-password-link"
            href={urlResolverGetForgotPassword(globalConfig)}
          >
            <FormattedMessage id="general.link.label.password.forgotten" />
          </Link>
        </div>
      </form>
    </>
  )
}
